import { AbstractModal } from '../AbstractModal';
import type { HexMapState } from '../../engine/render/renderer';
import type { ContentBuilder } from '../ui/ContentBuilder';
import type { Tile } from '../../engine/hex/types';
import { t } from '../../i18n/translations';
import { hexDistance } from '../../engine/hex/hexMath';
import { buildUnitRoster } from '../../engine/battle/roster';
import type { UnitGroup } from '../../engine/battle/units';
import {
    ACTION__FOUNDING__CONFIRM,
    ACTION__FOUNDING__FOOD,
    ACTION__FOUNDING__RESOURCE,
    ACTION__FOUNDING__UNIT,
} from '../../engine/input/actionIds';
import {
    RESOURCE_KEYS,
    applyCost,
    canAffordCost,
    getFreePopulation,
    getHouseCapacity,
    removeFreePeople,
    takeTools,
} from '../../engine/map/settlement';
import { getDeployedUnits } from '../../engine/scouting/utils';
import type { FoundingExpedition } from '../../engine/expedition/founding';

type ResourceSelection = Partial<Record<(typeof RESOURCE_KEYS)[number], number>>;

const RESOURCE_STEP = 5;

export class FoundingModal extends AbstractModal {
    selectedEscort = 0;
    selectedFood = 0;
    selectedResources: ResourceSelection = {};

    constructor(rect: { x: number; y: number; w: number; h: number }, tile: Tile) {
        super(tile, rect, null);
        this.id = `founding_modal_${tile.id}`;
        this.showTileHighlight = true;
        this.allowCloseDuringTutorial = true;
    }

    getHeaderLabel(state: HexMapState): string {
        return t(state.config.ui.language, 'founding.title');
    }

    handleAction(actionId: string, state: HexMapState): boolean {
        if (actionId.startsWith(`${ACTION__FOUNDING__UNIT}:`)) {
            const [, direction] = actionId.split(':');
            if (direction !== 'plus' && direction !== 'minus') return false;
            const current = this.selectedEscort;
            this.selectedEscort = direction === 'plus' ? current + 1 : Math.max(0, current - 1);
            return true;
        }
        if (actionId.startsWith(`${ACTION__FOUNDING__FOOD}:`)) {
            const [, direction] = actionId.split(':');
            if (direction !== 'plus' && direction !== 'minus') return false;
            const step = state.config.scouting.foodStep;
            this.selectedFood =
                direction === 'plus'
                    ? this.selectedFood + step
                    : Math.max(0, this.selectedFood - step);
            return true;
        }
        if (actionId.startsWith(`${ACTION__FOUNDING__RESOURCE}:`)) {
            const [, resourceKey, direction] = actionId.split(':');
            if (!resourceKey || (direction !== 'plus' && direction !== 'minus')) return false;
            const key = resourceKey as (typeof RESOURCE_KEYS)[number];
            const current = this.selectedResources[key] ?? 0;
            const step = key === 'tools' || key === 'weapons' ? 1 : RESOURCE_STEP;
            const next = direction === 'plus' ? current + step : Math.max(0, current - step);
            this.selectedResources[key] = next;
            return true;
        }
        if (actionId === ACTION__FOUNDING__CONFIRM) {
            return sendFoundingExpedition(state, this.tile, this);
        }
        return false;
    }

    buildContent(state: HexMapState, builder: ContentBuilder): void {
        const lang = state.config.ui.language;
        if (state.settlementByTileId[this.tile.id]) {
            builder.text(t(lang, 'founding.alreadySettled'));
            return;
        }
        if (!state.ownedTileIds.has(this.tile.id)) {
            builder.text(t(lang, 'founding.notControlled'));
            return;
        }
        if (
            state.foundingExpeditions.some((expedition) => expedition.targetTileId === this.tile.id)
        ) {
            builder.text(t(lang, 'founding.inProgress'));
            return;
        }
        const requiredPopulation = state.config.initial.settlement.population;
        const settlements = Object.values(state.settlementsById);
        const source = findSourceSettlement(settlements, requiredPopulation);
        if (!source) {
            builder.text(t(lang, 'founding.noPopulation'));
            return;
        }

        const distance = hexDistance(source.tilePosition, this.tile.position);
        const roster = buildUnitRoster(state.config);
        const deployed = getDeployedUnits(state, source.id);
        const availableMilitia = getAvailableMilitia(source, deployed.militia || 0);
        const escortGroup: UnitGroup[] = this.selectedEscort
            ? [{ type: 'militia', count: this.selectedEscort }]
            : [];
        const suggestedFood = suggestFoundingFood(
            requiredPopulation,
            escortGroup,
            distance,
            roster,
            state.config.expedition.foodPerPopPerHex,
        );

        builder.section(t(lang, 'founding.destination'), (section) => {
            section.text(t(lang, 'founding.tile', this.tile.id));
            section.text(t(lang, 'founding.distance', distance));
            section.text(t(lang, 'founding.source', source.name));
        });

        builder.section(t(lang, 'founding.population'), (section) => {
            section.text(t(lang, 'founding.populationRequired', requiredPopulation));
            section.text(t(lang, 'founding.populationFree', getFreePopulation(source)));
        });

        builder.section(t(lang, 'founding.escort'), (section) => {
            section.text(t(lang, 'founding.escortSelected', this.selectedEscort, availableMilitia));
            section.button(`${ACTION__FOUNDING__UNIT}:plus`, t(lang, 'founding.unitPlus'), true);
            section.button(
                `${ACTION__FOUNDING__UNIT}:minus`,
                t(lang, 'founding.unitMinus'),
                this.selectedEscort > 0,
            );
        });

        builder.section(t(lang, 'founding.food'), (section) => {
            section.text(t(lang, 'founding.foodAvailable', source.inventory.food));
            section.text(t(lang, 'founding.foodSelected', this.selectedFood));
            section.text(t(lang, 'founding.foodSuggested', suggestedFood));
            section.button(`${ACTION__FOUNDING__FOOD}:plus`, t(lang, 'founding.foodPlus'), true);
            section.button(
                `${ACTION__FOUNDING__FOOD}:minus`,
                t(lang, 'founding.foodMinus'),
                this.selectedFood > 0,
            );
        });

        builder.section(t(lang, 'founding.supplies'), (section) => {
            section.text(t(lang, 'founding.toolsRequired', state.config.expedition.toolsRequired));
            RESOURCE_KEYS.filter((key) => key !== 'food').forEach((key) => {
                const label = t(lang, `resource.${key}`);
                const available = getResourceAvailable(source, key);
                const selected = clampSelection(this.selectedResources[key] ?? 0, available);
                if (selected !== (this.selectedResources[key] ?? 0)) {
                    this.selectedResources[key] = selected;
                }
                section.text(t(lang, 'founding.resourceLine', label, selected, available));
                section.button(
                    `${ACTION__FOUNDING__RESOURCE}:${key}:plus`,
                    t(lang, 'founding.resourcePlus'),
                    selected < available,
                );
                section.button(
                    `${ACTION__FOUNDING__RESOURCE}:${key}:minus`,
                    t(lang, 'founding.resourceMinus'),
                    selected > 0,
                );
                section.spacer(4);
            });
        });

        const canSend = canSendFounders(
            state,
            source,
            this.selectedFood,
            this.selectedEscort,
            this.selectedResources,
            deployed,
        );
        builder.section(t(lang, 'founding.launch'), (section) => {
            section.button(ACTION__FOUNDING__CONFIRM, t(lang, 'founding.send'), canSend);
        });
    }
}

function findSourceSettlement(
    settlements: HexMapState['settlementsById'][string][],
    requiredPopulation: number,
): HexMapState['settlementsById'][string] | null {
    let best: HexMapState['settlementsById'][string] | null = null;
    let bestFree = 0;
    for (const settlement of settlements) {
        const free = getFreePopulation(settlement);
        if (free >= requiredPopulation && free > bestFree) {
            best = settlement;
            bestFree = free;
        }
    }
    return best;
}

function getAvailableMilitia(
    settlement: HexMapState['settlementsById'][string],
    deployed: number,
): number {
    return Math.max(0, settlement.jobs.militia - deployed);
}

function getResourceAvailable(
    settlement: HexMapState['settlementsById'][string],
    key: (typeof RESOURCE_KEYS)[number],
): number {
    if (key === 'tools') return settlement.inventory.tools.length;
    return settlement.inventory[key] ?? 0;
}

function clampSelection(value: number, available: number): number {
    if (!Number.isFinite(value)) return 0;
    return Math.max(0, Math.min(available, value));
}

function suggestFoundingFood(
    population: number,
    escort: UnitGroup[],
    distance: number,
    roster: ReturnType<typeof buildUnitRoster>,
    foodPerPopPerHex: number,
): number {
    const escortFood = escort.reduce((sum, group) => {
        const unit = roster.get(group.type);
        if (!unit) return sum;
        return sum + unit.foodPerHex * group.count;
    }, 0);
    const total = population * foodPerPopPerHex + escortFood;
    return Math.max(0, Math.ceil(distance * total));
}

function canSendFounders(
    state: HexMapState,
    settlement: HexMapState['settlementsById'][string],
    food: number,
    escortCount: number,
    resources: ResourceSelection,
    deployed: Record<string, number>,
): boolean {
    const requiredPopulation = state.config.initial.settlement.population;
    const housesRequired = Math.ceil(requiredPopulation / getHouseCapacity());
    const houseCost = multiplyCost(state.config.buildings.woodHouse.cost || {}, housesRequired);
    if (getFreePopulation(settlement) < requiredPopulation) return false;
    if (escortCount < 0) return false;
    const availableMilitia = getAvailableMilitia(settlement, deployed.militia || 0);
    if (escortCount > availableMilitia) return false;
    if (settlement.inventory.food < food) return false;
    const toolsSelected = resources.tools ?? 0;
    if (toolsSelected < state.config.expedition.toolsRequired) return false;
    if (!canAffordCost(settlement, mergeCosts(resources as Record<string, number>, houseCost))) {
        return false;
    }
    const roster = buildUnitRoster(state.config);
    const weaponCost = (roster.get('militia')?.weaponCost || 0) * escortCount;
    const extraWeapons = resources.weapons ?? 0;
    return settlement.inventory.weapons >= weaponCost + extraWeapons;
}

function sendFoundingExpedition(state: HexMapState, target: Tile, modal: FoundingModal): boolean {
    if (state.settlementByTileId[target.id]) return false;
    if (!state.ownedTileIds.has(target.id)) return false;
    if (state.foundingExpeditions.some((expedition) => expedition.targetTileId === target.id)) {
        return false;
    }
    const requiredPopulation = state.config.initial.settlement.population;
    const housesRequired = Math.ceil(requiredPopulation / getHouseCapacity());
    const houseCost = multiplyCost(state.config.buildings.woodHouse.cost || {}, housesRequired);
    const settlements = Object.values(state.settlementsById);
    const source = findSourceSettlement(settlements, requiredPopulation);
    if (!source) return false;
    const deployed = getDeployedUnits(state, source.id);
    if (
        !canSendFounders(
            state,
            source,
            modal.selectedFood,
            modal.selectedEscort,
            modal.selectedResources,
            deployed,
        )
    ) {
        return false;
    }

    const roster = buildUnitRoster(state.config);
    const escortWeaponsIssued = (roster.get('militia')?.weaponCost || 0) * modal.selectedEscort;
    const cargoWeapons = modal.selectedResources.weapons ?? 0;
    const toolsSelected = modal.selectedResources.tools ?? 0;
    const totalWeapons = escortWeaponsIssued + cargoWeapons;
    if (source.inventory.weapons < totalWeapons) return false;
    if (source.inventory.food < modal.selectedFood) return false;

    source.inventory.food -= modal.selectedFood;
    if (escortWeaponsIssued > 0) {
        applyCost(source, { weapons: escortWeaponsIssued });
    }
    if (toolsSelected > 0) takeTools(source, toolsSelected);
    if (Object.keys(houseCost).length > 0) applyCost(source, houseCost);

    const resourceCost: Record<string, number> = {};
    RESOURCE_KEYS.filter((key) => key !== 'food' && key !== 'tools' && key !== 'weapons').forEach(
        (key) => {
            const amount = clampSelection(
                modal.selectedResources[key] ?? 0,
                getResourceAvailable(source, key),
            );
            if (amount > 0) resourceCost[key] = amount;
        },
    );
    if (cargoWeapons > 0) resourceCost.weapons = cargoWeapons;
    if (Object.keys(resourceCost).length > 0) applyCost(source, resourceCost);

    removeFreePeople(source, requiredPopulation);
    state.expeditionIdCounter += 1;
    const distance = hexDistance(source.tilePosition, target.position);
    const totalTicks = Math.max(
        1,
        Math.round(
            state.config.expedition.baseTicks + distance * state.config.expedition.ticksPerHex,
        ),
    );

    const expedition: FoundingExpedition = {
        id: `founding_${state.expeditionIdCounter}`,
        fromSettlementId: source.id,
        targetTileId: target.id,
        population: requiredPopulation,
        foodSupplies: modal.selectedFood,
        toolsSupplies: toolsSelected,
        escortUnits: modal.selectedEscort ? [{ type: 'militia', count: modal.selectedEscort }] : [],
        escortWeaponsIssued,
        cargo: {
            ...resourceCost,
            weapons: cargoWeapons,
        },
        totalTicks,
        progress: 0,
        distance,
        status: 'traveling',
        createdAt: state.simulation.tick,
    };
    state.foundingExpeditions.push(expedition);
    return true;
}

function multiplyCost(cost: Record<string, number>, multiplier: number): Record<string, number> {
    const scaled: Record<string, number> = {};
    Object.entries(cost).forEach(([key, amount]) => {
        scaled[key] = amount * multiplier;
    });
    return scaled;
}

function mergeCosts(
    base: Record<string, number>,
    extra: Record<string, number>,
): Record<string, number> {
    const merged = { ...base };
    Object.entries(extra).forEach(([key, amount]) => {
        merged[key] = (merged[key] || 0) + amount;
    });
    return merged;
}
